import type { ReactNode } from 'react';
import { Tooltip } from '@odo/shared-ui';
import { TOOLTIP_OVER_MODAL } from '../../../../shared/ModalShell';
import { MARKUP_COLORS, MARKUP_SIZES, markupSizeLabel, type MarkupSize } from './markupStyle';

const FOCUS_RING = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400';

interface ColorSwatchesProps {
    value: string;
    onChange: (color: string) => void;
}

function ColorSwatches({ value, onChange }: ColorSwatchesProps) {
    return (
        <div role="radiogroup" aria-label="Colour" className="inline-flex items-center gap-2">
            {MARKUP_COLORS.map((color) => {
                const selected = color.value === value;
                return (
                    <Tooltip key={color.id} content={color.label} className={TOOLTIP_OVER_MODAL}>
                        <button
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            aria-label={color.label}
                            onClick={() => onChange(color.value)}
                            className={`w-6 h-6 rounded-full border border-black/15 transition-shadow ${FOCUS_RING} focus-visible:ring-offset-2 ${
                                selected
                                    ? 'ring-2 ring-offset-2 ring-gray-900'
                                    : 'hover:ring-2 hover:ring-offset-1 hover:ring-gray-300'
                            }`}
                            style={{ backgroundColor: color.value }}
                        />
                    </Tooltip>
                );
            })}
        </div>
    );
}

// Bar thickness per size, in CSS pixels: the button shows the stroke it draws.
const SIZE_BAR_PX: Record<MarkupSize, number> = { small: 2, medium: 4, large: 7 };

interface SizePickerProps {
    value: MarkupSize;
    onChange: (size: MarkupSize) => void;
}

function SizePicker({ value, onChange }: SizePickerProps) {
    return (
        <div
            role="radiogroup"
            aria-label="Stroke and text size"
            className="inline-flex items-center gap-0.5 rounded-lg border border-gray-200 bg-white p-0.5"
        >
            {MARKUP_SIZES.map((size) => {
                const selected = size === value;
                return (
                    <Tooltip
                        key={size}
                        content={markupSizeLabel(size)}
                        className={TOOLTIP_OVER_MODAL}
                    >
                        <button
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            aria-label={markupSizeLabel(size)}
                            onClick={() => onChange(size)}
                            className={`h-8 w-10 rounded-md inline-flex items-center justify-center transition-colors ${FOCUS_RING} ${
                                selected
                                    ? 'bg-gray-900 text-white'
                                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                            }`}
                        >
                            <span
                                aria-hidden="true"
                                className="w-5 rounded-full bg-current"
                                style={{ height: SIZE_BAR_PX[size] }}
                            />
                        </button>
                    </Tooltip>
                );
            })}
        </div>
    );
}

interface IconActionProps {
    label: string;
    tooltip: string;
    disabled: boolean;
    onClick: () => void;
    children: ReactNode;
}

function IconAction({ label, tooltip, disabled, onClick, children }: IconActionProps) {
    return (
        <Tooltip content={tooltip} className={TOOLTIP_OVER_MODAL} disabled={disabled}>
            <button
                type="button"
                aria-label={label}
                onClick={onClick}
                disabled={disabled}
                className={`w-9 h-9 rounded-lg inline-flex items-center justify-center text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:bg-transparent ${FOCUS_RING}`}
            >
                {children}
            </button>
        </Tooltip>
    );
}

function BarDivider() {
    return <span aria-hidden="true" className="h-6 w-px bg-gray-200" />;
}

export { BarDivider, ColorSwatches, IconAction, SizePicker };
